// frontend/src/components/NotificationBell.js
import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { fetchNotifications, markNotificationRead, markAllNotificationsRead, deleteAllNotifications, deleteNotificationsBatch, fetchNotificationAudio } from '../api';
import { AuthContext } from '../context/AuthContext';
import { analyzeNotification } from '../utils/notificationActions';
import SuggestedActionModal from './SuggestedActionModal';
import './NotificationBell.css';
import { useNotification } from '../context/NotificationContext';
import { useI18n } from '../i18n';
import { useAgentCommands } from '../contexts/AgentCommandContext';

export default function NotificationBell() {
  const { addConfirm } = useNotification();
  const { t, language } = useI18n();
  const { registerCommand, unregisterCommand } = useAgentCommands();
  const { user } = useContext(AuthContext);
  const [count, setCount] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [list, setList] = useState([]);
  const containerRef = useRef();
  const context = typeof window !== 'undefined' ? window.location.pathname.split('/')[1] || '' : '';

  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedAction, setSelectedAction] = useState(null);

  const loadNotifications = useCallback(async () => {
    if (!user) return [];
    try {
      const res = await fetchNotifications(context);
      const data = Array.isArray(res?.data) ? res.data : (res?.data?.notifications || []);
      const safeData = Array.isArray(data) ? data : [];
      setList(safeData);
      // If the backend returns pagination metadata, we should ideally sum unread across pages
      // However, since we now cap at 50, we calculate unread from the current view.
      setCount(safeData.filter(n => n.status === 'unread').length);
      return safeData;
    } catch (err) { return []; }
  }, [user, context]);

  useEffect(() => {
    loadNotifications();
    const iv = setInterval(loadNotifications, 60000);
    return () => clearInterval(iv);
  }, [loadNotifications]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
        if (menuOpen && containerRef.current && !containerRef.current.contains(e.target)) {
            setMenuOpen(false);
            setDeleteMode(false); // Reset mode on close
            setSelectedIds(new Set());
        }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [menuOpen]);


  const handleMarkRead = async (id, e) => {
    e.stopPropagation();
    await markNotificationRead(id);
    loadNotifications();
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      loadNotifications();
    } catch (error) {
      console.error('Error marking all read:', error);
    }
  };

  const toggleDeleteMode = () => {
      setDeleteMode(!deleteMode);
      setSelectedIds(new Set());
  };

  const toggleSelect = (id) => {
      const next = new Set(selectedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setSelectedIds(next);
  };

  const toggleSelectAll = () => {
      if (selectedIds.size === list.length && list.length > 0) {
          setSelectedIds(new Set());
      } else {
          setSelectedIds(new Set(list.map(n => n.id)));
      }
  };

  const handleExecuteDelete = async () => {
      if (selectedIds.size === 0) return;

      const confirmMsg = selectedIds.size === list.length
        ? t('notifications.confirmDeleteAll')
        : t('notifications.confirmDeleteSome', { count: selectedIds.size });

      addConfirm(confirmMsg, async () => {
        try {
            if (selectedIds.size === list.length) {
                await deleteAllNotifications();
            } else {
                await deleteNotificationsBatch(Array.from(selectedIds));
            }
            await loadNotifications();
            setDeleteMode(false);
            setSelectedIds(new Set());
        } catch (e) {
            console.error('Error deleting:', e);
        }
      });
  };

  const handleNotificationClick = (n) => {
    if (deleteMode) {
      toggleSelect(n.id);
      return;
    }

    // Extraer el texto de forma segura
    let rawText = '';
    if (typeof n.message === 'string') rawText = n.message;
    else if (typeof n.title === 'string') rawText = n.title;
    else if (n.message && n.message.text) rawText = n.message.text;

    const action = analyzeNotification(rawText, language);

    if (action) {
      setSelectedAction({ notification: n, action });
    }

    if (n.status === 'unread') {
      markNotificationRead(n.id).then(() => loadNotifications());
    }
  };

  const handlePlayAudio = async (id, e) => {
    e.stopPropagation();
    try {
      const res = await fetchNotificationAudio(id);
      const url = URL.createObjectURL(res.data);
      new Audio(url).play();
    } catch (e) { console.error('Error audio', e); }
  };

  const renderMsg = useCallback((n) => {
      let msg = n.message || n.title;
      if (msg === '[object Object]') return t('notifications.systemNew');
      if (typeof msg === 'object') return msg.text || msg.content || JSON.stringify(msg);
      return msg || t('notifications.fallback');
  }, [t]);

  useEffect(() => {
    registerCommand('notifications_open', async () => {
      const data = await loadNotifications();
      setMenuOpen(true);
      setDeleteMode(false);
      setSelectedIds(new Set());
      return { ok: true, count: data.length, unread: data.filter(n => n.status === 'unread').length };
    });

    registerCommand('notifications_close', () => {
      setMenuOpen(false);
      setDeleteMode(false);
      setSelectedIds(new Set());
      return { ok: true };
    });

    registerCommand('notifications_read', async () => {
      const data = await loadNotifications();
      setMenuOpen(true);
      setDeleteMode(false);
      const text = data.slice(0, 5).map(renderMsg).filter(Boolean).join('. ');
      return { ok: true, text };
    });

    registerCommand('notifications_mark_read', async () => {
      await markAllNotificationsRead();
      await loadNotifications();
      return { ok: true };
    });

    registerCommand('notifications_delete_all', async () => {
      await deleteAllNotifications();
      await loadNotifications();
      setDeleteMode(false);
      setSelectedIds(new Set());
      return { ok: true };
    });

    return () => {
      unregisterCommand('notifications_open');
      unregisterCommand('notifications_close');
      unregisterCommand('notifications_read');
      unregisterCommand('notifications_mark_read');
      unregisterCommand('notifications_delete_all');
    };
  }, [loadNotifications, registerCommand, renderMsg, unregisterCommand]);

  return (
    <div className="notification-bell-container" ref={containerRef}>
      <button
        className="bell-button"
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label={t('notifications.title')}
      >
        <span className="bell-icon">🔔</span>
        {count > 0 && <span className="bell-count">{count}</span>}
      </button>

      {menuOpen && (
        <div className="bell-dropdown">
          <div className="bell-header">
            <strong>{t('notifications.title')}</strong>
            <div className="header-actions">
                <button className="header-action-btn" onClick={handleMarkAllRead} title={t('notifications.markAllRead')}>
                    👓
                </button>
                <button
                    className={`header-action-btn ${deleteMode ? 'active' : ''}`}
                    onClick={toggleDeleteMode}
                    title={deleteMode ? t('notifications.cancelDelete') : t('notifications.deleteNotifications')}
                >
                    {deleteMode ? '❌' : '🗑️'}
                </button>
            </div>
          </div>

          {deleteMode && list.length > 0 && (
              <div className="bell-subheader">
                  <label className="select-all-label">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === list.length}
                        onChange={toggleSelectAll}
                      />
                      <span>{t('notifications.selectAll')}</span>
                  </label>
              </div>
          )}

          <div className="bell-list">
            {list.length === 0 && <div className="bell-empty">{t('notifications.empty')}</div>}
            {list.map(n => (
              <div
                key={n.id}
                className={`bell-item ${n.status}`}
                onClick={() => handleNotificationClick(n)}
                style={{ cursor: 'pointer' }}
              >
                {deleteMode && (
                    <div className="bell-item-check">
                        <input
                            type="checkbox"
                            checked={selectedIds.has(n.id)}
                            onChange={(e) => {
                                e.stopPropagation();
                                toggleSelect(n.id);
                            }}
                        />
                    </div>
                )}
                <div className="bell-item-content">
                    <p className="bell-item-text">{renderMsg(n)}</p>
                    <span className="bell-item-date">{new Date(n.createdAt || Date.now()).toLocaleDateString()}</span>
                </div>
                {!deleteMode && (
                    <div className="bell-item-actions">
                    <button onClick={(e) => { e.stopPropagation(); handlePlayAudio(n.id, e); }} title={t('common.playAudio')} className="action-btn">🔊</button>
                    {n.status === 'unread' && (
                        <button onClick={(e) => { e.stopPropagation(); handleMarkRead(n.id, e); }} title={t('notifications.markRead')} className="action-btn">✓</button>
                    )}
                    </div>
                )}
              </div>
            ))}
          </div>

          {deleteMode && list.length > 0 && (
              <div className="bell-footer">
                  <button
                    className="delete-execute-btn"
                    onClick={handleExecuteDelete}
                    disabled={selectedIds.size === 0}
                    title={t('notifications.deleteSelected')}
                  >
                      🗑️ {t('notifications.deleteSelectedButton', { count: selectedIds.size })}
                  </button>
              </div>
          )}
        </div>
      )}

      {selectedAction && (
        <SuggestedActionModal
          notification={selectedAction.notification}
          action={selectedAction.action}
          onClose={() => setSelectedAction(null)}
          onExecute={() => {
            setSelectedAction(null);
            setMenuOpen(false); // Cierra el dropdown de notificaciones
          }}
        />
      )}
    </div>
  );
}
